import { render } from "preact";
import { useState, useEffect } from "preact/hooks";
import markdownit from 'markdown-it'
import mvsp from "../mvsp/mvsp_v3.0-20231109-catalog.json"
import { mkConfig, generateCsv, download } from "export-to-csv";

const ANSWERS_KEY = "mvsp-answers"

const md = markdownit()

function getSavedAnswers() {
  const serializedAnswers = localStorage.getItem(ANSWERS_KEY)
  const defaultAnswers = {
    company: undefined,
    date: undefined,
    answers: {}
  }
  if (serializedAnswers) {
    try {
      const parsedAnswers = JSON.parse(serializedAnswers)
      return parsedAnswers
    } catch (err) {
      console.warn("Error loading saved answers", err)
      return defaultAnswers
    }
  }
  return defaultAnswers
}

function generateCSV(answers) {
  const rows = []

  const csvConfig = mkConfig({
    columnHeaders: ["Control ID", "Control Name", "Description", "Answer", "Comments"],
    filename: `MVSP - ${answers.organization || 'Unknown Organization'} ${new Date().toISOString().split("T")[0]}`
  })

  mvsp.catalog.groups.forEach(g => {
    g.controls.forEach(c => {
      const answer = answers.answers[c.id]
      rows.push({
        "Control ID": c.id,//.props.find(p => p.name === "label").value,
        "Control Name": c.title,
        "Description": c.parts.find(p => p.name === "statement").prose,
        "Answer": answer?.response || '',
        "Comments": answer?.comment || '',
      })
    })
  })
  const csv = generateCsv(csvConfig)(rows)
  download(csvConfig)(csv)
}


const Answer = ({ id, organization, currentAnswer, updateAnswer }) => {
  const updateSelection = (val) => {
    updateAnswer({
      ...currentAnswer,
      response: val === "Choose one" ? undefined : val
    })
  }
  const updateComment = (val) => {
    updateAnswer({
      ...currentAnswer,
      comment: val
    })
  }
  let answerClasses = ""
  if (currentAnswer?.response === "Yes") {
    answerClasses = "bg-green-100"
  } else if (currentAnswer?.response === "No") {
    answerClasses = "bg-red-100"
  } else if (currentAnswer?.response === "Partially") {
    answerClasses = "bg-amber-100"
  } else if (currentAnswer?.response === "Not Applicable") {
    answerClasses = "bg-gray-300"
  } else {
    answerClasses = "bg-gray-100"
  }

  return (
    <div>
      <div class="mb-2 bg-gr text-sm md:text-base">Does {organization || 'this organization'} implement this control?</div>

      <select name={`select-${id}`} class={`text-sm md:text-base p-2 ${answerClasses}`} onChange={(e) => updateSelection(e.currentTarget.value)}>
        <option default>Choose one</option>
        <option selected={currentAnswer?.response === "Yes"}>Yes</option>
        <option selected={currentAnswer?.response === "No"}>No</option>
        <option selected={currentAnswer?.response === "Partially"}>Partially</option>
        <option selected={currentAnswer?.response === "Not Applicable"}>Not Applicable</option>
      </select>

      <label class="block mt-4 text-sm md:text-base" for={`comment-${id}`}>Comments</label>
      <textarea class="mt-2 p-2 bg-gray-100 h-20 w-full md:w-md text-sm md:text-base" name={`comment-${id}`} onKeyUp={(e) => updateComment(e.currentTarget.value)}>{currentAnswer?.comment}</textarea>
    </div>
  )
}

const VendorAssessment = () => {
  const [currentAnswers, setCurrentAnswers] = useState(getSavedAnswers)

  useEffect(() => {
    const serializedAnswers = JSON.stringify(currentAnswers)
    localStorage.setItem(ANSWERS_KEY, serializedAnswers)
  }, [currentAnswers])

  function updateAnswer(key, answer) {
    const newAnswer = { [key]: answer }
    setCurrentAnswers({...currentAnswers, answers: { ...currentAnswers.answers, ...newAnswer } })
  }

  function updateOrg(val) {
    setCurrentAnswers({
      ...currentAnswers,
      organization: val
    })
  }

  function clearAnswers() {
    setCurrentAnswers({ answers: {}})
  }

  function exportCSV() {
    const csv = generateCSV(currentAnswers)
    console.log("csv is", csv)
  }

  return (
    <div class="bg-gray-300 md:p-8 bg-monorail">
    <div class="p-3 md:p-10 font-mono max-w-5xl m-auto bg-white md:rounded-lg">

      <div class="md:mb-6 flex justify-center items-center">
        <img class="h-6 md:h-12 max-w-full" src="/monorail-technology.png" alt="Monorail Technology" />
        <span
            style={{
                color: "#9901ff",
                fontSize: "2em",
                padding: "15px",
            }}>&times;</span>
        <span class="text-xl md:text-4xl font-bold">MVSP</span>
      </div>

      <p class="mb-4 prose prose-sm md:prose-base max-w-none">
        This is a free tool provided by <a href="https://monorail.tech">Monorail Technology</a>{" "}
        for answering the <a href="https://mvsp.dev/">Minimum Viable Secure Product</a> security controls questionnaire. It is currently using version {mvsp.catalog.metadata.version}, which was last modified on {new Date(mvsp.catalog.metadata["last-modified"]).toLocaleDateString()}.
      </p>

      <p class="mb-4 prose prose-sm md:prose-base max-w-none">
        Fill out the answers completely or partially, and then download a CSV file to edit and/or share.
      </p>

      <p class="mb-4 prose prose-sm md:prose-base max-w-none">
        It will save your answers locally in the browser, so feel free to come back later to finish.
      </p>

      <p class="text-gray-300">--</p>

      <h1 class="text-sm md:text-2xl mt-8 mb-6">
        Answers for
        <input class="ml-4 px-4 py-2 bg-gray-100 md:w-md" type="text" value={currentAnswers.organization || ''} onKeyUp={e => updateOrg(e.currentTarget.value)} placeholder="Your organization name"/>
      </h1>

      <button class="bg-gray-100 hover:text-black hover:bg-gray-200 text-sm align-middle p-2 mb-4 cursor-pointer" onClick={clearAnswers}>Clear answers</button>
      <button class="bg-gray-100 hover:text-black hover:bg-gray-200 text-sm align-middle p-2 ml-2 mb-4 cursor-pointer" onClick={exportCSV}>Download CSV</button>

      <table class="text-left border-collapse border border-gray-300 md:table-auto">
      { mvsp.catalog.groups.map(g => (
        <>
        <tr class="bg-gray-50 border border-gray-200">
          <td class="p-3" colSpan="2">
          <h2 class="text-md md:text-xl">{g.props.find(p => p.name === "label").value} {g.title}</h2>
          </td>
        </tr>
        <tr class="bg-gray-50">
          <th class="p-3 text-sm md:text-base font-normal border border-gray-200 w-1/5">Control</th>
          <th class="p-3 text-sm md:text-base font-normal border border-gray-200">Description</th>
        </tr>
        {g.controls.map(c => (
          <>
          <tr>
            <td class="p-3 align-top border border-gray-200 md:w-1/4">
              <h3 class="text-sm md:text-base">{c.props.find(p => p.name === "label").value} {c.title}</h3>
            </td>
            <td class="p-3 border border-gray-200">
              <div class="prose prose-sm md:prose-base max-w-none prose-blockquote:border-none prose-blockquote:not-italic prose-blockquote:mt-0 prose-blockquote:text-base-content prose-li:mt-0 prose-li:mb-3 prose-li:last:mb-0" dangerouslySetInnerHTML={{ __html: md.render(c.parts.find(p => p.name === "statement").prose) }} />

              {c.parts.filter(p => p.name === "guidance").map(g => (
                <details class="text-sm mt-3 opacity-70 hover:opacity-100 open:opacity-100">
                  <summary class="list-none cursor-pointer">{g.title}</summary>
                  <div class="pl-4 prose prose-sm max-w-none mt-2 opacity-100 prose-blockquote:border-none prose-blockquote:not-italic prose-blockquote:mt-0 prose-blockquote:text-base-content prose-li:mt-0 prose-li:mb-3 prose-li:last:mb-0" dangerouslySetInnerHTML={{ __html: md.render(g.prose) }}></div>
                </details>
              ))}
            </td>
          </tr>
          <tr>
            <td></td>
            <td class="p-3 border border-gray-200 md:text-lg">
                <Answer id={c.id} organization={currentAnswers.organization} currentAnswer={currentAnswers.answers[c.id]} updateAnswer={(newAnswer) => updateAnswer(c.id, newAnswer) } />
            </td>
          </tr>
          </>
        ))}
        </>
      ))}
      </table>
      <button class="bg-gray-100 hover:text-black hover:bg-gray-200 text-sm align-middle p-2 mt-4 cursor-pointer" onClick={exportCSV}> Download CSV</button>

    </div>
    </div>)
}

render(<VendorAssessment />, document.querySelector("main"));
